/* ---------------------------------------------------------------------------
   Every experiment the web funnel can run.

   One registry doing two jobs. It says which PostHog flag decides which piece
   of presentation, and it is the analytics allowlist for variant properties: a
   variant this file does not declare can neither be rendered nor reported. A
   flag renamed, retyped, or rolled out to a value nobody wrote here degrades to
   the control arm instead of blanking a screen or inventing a property value.

   The control arm is always the first variant, and it is what a visitor gets
   when analytics is off, the flag is missing, or the network never answers.
   Adding an experiment is one entry here plus the switch that reads it — never
   a free-form flag key at a call site.
--------------------------------------------------------------------------- */

type ExperimentDefinition = {
  /** The feature-flag key as it is spelled in the PostHog project. */
  flag: string;
  /** Every value the flag may return, control first. */
  variants: readonly [string, ...string[]];
  /** What the experiment is for, in one line. Read by the /dev board. */
  purpose: string;
};

export const experiments = {
  "landing-message": {
    flag: "vessa-landing-message",
    variants: ["control", "appointment"],
    purpose: "Landing headline: history framing against appointment framing.",
  },
  "paywall-offer": {
    flag: "vessa-paywall-offer",
    variants: ["control", "per-day"],
    purpose: "Offer screen: plain prices against a per-day price with a badge.",
  },
} as const satisfies Record<string, ExperimentDefinition>;

export type ExperimentKey = keyof typeof experiments;

export type Variant<K extends ExperimentKey = ExperimentKey> =
  (typeof experiments)[K]["variants"][number];

export const experimentKeys = Object.keys(experiments) as ExperimentKey[];

export function isExperimentKey(value: unknown): value is ExperimentKey {
  return typeof value === "string" && Object.hasOwn(experiments, value);
}

/** The arm a visitor gets when nothing else answers. */
export function controlOf<K extends ExperimentKey>(key: K): Variant<K> {
  return experiments[key].variants[0] as Variant<K>;
}

export function isVariantOf<K extends ExperimentKey>(key: K, value: unknown): value is Variant<K> {
  return (experiments[key].variants as readonly unknown[]).includes(value);
}

/** A raw flag value from PostHog, narrowed to something this funnel can render. */
export function resolveVariant<K extends ExperimentKey>(key: K, flagValue: unknown): Variant<K> {
  return isVariantOf(key, flagValue) ? flagValue : controlOf(key);
}
